import { ReactNode, SyntheticEvent } from 'react'
import { FiChevronLeft } from 'react-icons/fi'

import { money } from 'utilities/helper/format'

/**
 * The shared chrome for the cards on the صندوق‌ها tab.
 *
 * The moneypool and cashbox cards were two independent transcriptions of the
 * same Android layout, each carrying its own hard-coded hexes, its own radius
 * and its own shadow — one `shadow-card`, one an inline
 * `0 2px 8px rgba(0,0,0,0.12)` that reads as a drop shadow from 2013. Next to
 * the home page, which is white `rounded-card` surfaces on `canvas` separated
 * by a `divider` hairline, they looked like a different product.
 *
 * So the chrome lives here once and both cards compose it. The rule for
 * anything added later: no literal colours, no literal radii — use the tokens
 * in `tailwind.config.js`, which is what home does.
 *
 * ## Type scale
 *
 * The first pass sat everything between 11px and 14px at weight 400, and read
 * thin and undersized — a card that is the whole point of the screen was
 * quieter than the caption above it. The green hero next to it works because
 * it has real contrast: a 12px label against a 26px bold number.
 *
 * So the card has that contrast too, at card scale:
 *
 *   title      16 / 700     the fund's name is the heading, not a list item
 *   subtitle   13 / 500
 *   label      14 / 400     `text-secondary`, deliberately the quiet half
 *   value      15 / 500     700 + `heading` for the row that carries money
 *   chip       12 / 700
 *
 * IRANYekanX ships 100–1000, so 500 is a real Medium cut here — not a weight
 * the browser synthesises. Check `assets/fonts/iranyekan.scss` before reaching
 * for a weight that isn't in that list.
 */

export function FundCard({
    onClick,
    children,
}: {
    onClick: () => void
    children: ReactNode
}) {
    return (
        <div
            onClick={onClick}
            className="bg-white border border-divider rounded-card shadow-card p-4 text-right cursor-pointer active:scale-[0.995] transition-transform"
        >
            {children}
        </div>
    )
}

/**
 * Avatar, name, one line of context, and a chevron.
 *
 * The chevron is not decoration: the whole card is tappable and nothing else
 * on it says so. It matches the managed-funds rows on home, which is where a
 * member learns the convention.
 *
 * Avatar is 48px, down from the original 60px. At 60 it outweighed the fund's
 * own name; at 44 it looked undersized once the name grew to 16px.
 */
export function FundCardHeader({
    avatar,
    title,
    subtitle,
    onAvatarError,
}: {
    avatar: string
    title: string
    subtitle: ReactNode
    onAvatarError?: (event: SyntheticEvent<HTMLImageElement>) => void
}) {
    return (
        <div className="flex items-center gap-3">
            <img
                src={avatar}
                alt=""
                onError={onAvatarError}
                className="w-12 h-12 rounded-full border border-divider bg-sunken object-cover shrink-0"
            />
            <div className="flex-1 min-w-0">
                <p className="text-base font-bold text-heading truncate leading-snug">
                    {title}
                </p>
                <p className="text-[13px] font-medium text-text-secondary mt-[2px] truncate">
                    {subtitle}
                </p>
            </div>
            <FiChevronLeft className="w-5 h-5 text-text-muted shrink-0" />
        </div>
    )
}

/** A hairline-separated block inside a card. */
export function FundSection({ children }: { children: ReactNode }) {
    return <div className="mt-3 pt-3 border-t border-divider">{children}</div>
}

/** Label on the right, value on the left — the page is already RTL. */
export function FundRow({
    label,
    value,
    strong,
}: {
    label: string
    value: ReactNode
    strong?: boolean
}) {
    return (
        <div className="flex items-baseline justify-between gap-3">
            <span className="text-sm text-text-secondary shrink-0">
                {label}
            </span>
            <span
                className={`text-[15px] truncate ${
                    strong
                        ? 'font-bold text-heading'
                        : 'font-medium text-text-body'
                }`}
            >
                {value}
            </span>
        </div>
    )
}

/**
 * A toman figure, formatted once and bidi-safe.
 *
 * Two bugs in one. These cards called `Number(x).toLocaleString()` with no
 * locale, so grouping followed the browser's locale and disagreed with the
 * fa-IR figures on home — same amount, two renderings, one app.
 *
 * And the sign on a negative balance landed in the wrong place. Digits are
 * bidi-neutral, so «-12,316,334 تومان» in an RTL run reorders to put the minus
 * between the number and تومان, where a Persian reader — going right to left —
 * meets the digits first and the sign second. So the magnitude is isolated in
 * its own `<bdi>` and the sign is emitted separately *before* it: in RTL flow
 * that puts the sign to the right of the digits, i.e. first in reading order,
 * which is where a minus belongs.
 */
export function Amount({ value }: { value: number | string }) {
    const amount = Number(value) || 0
    return (
        <>
            {amount < 0 && <bdi>−</bdi>}
            <bdi>{money(Math.abs(amount))}</bdi> تومان
        </>
    )
}

/** Status pill. Same shape as the badges on the home payables card. */
export function Chip({
    bg,
    color,
    icon,
    label,
}: {
    bg: string
    color: string
    icon?: string
    label: string
}) {
    return (
        <span
            className={`inline-flex items-center gap-1 rounded-full py-1 text-xs font-bold shrink-0 ${
                icon ? 'pl-3 pr-2' : 'px-3'
            }`}
            style={{ backgroundColor: bg, color }}
        >
            {icon && <img src={icon} alt="" className="w-4 h-4 shrink-0" />}
            <span>{label}</span>
        </span>
    )
}

/** «حق عضویت:» ... chip — one status line. */
export function StatusLine({
    label,
    children,
}: {
    label: string
    children: ReactNode
}) {
    return (
        <div className="flex items-center justify-between gap-3">
            <span className="text-sm font-medium text-text-body">{label}</span>
            {children}
        </div>
    )
}

/**
 * The pay CTA. A pill, like every other primary action in the app — the old
 * 10px-radius 18sp block was the loudest thing on the screen and appeared on
 * as many cards as the member had unpaid funds.
 */
export function FundPayButton({ onClick }: { onClick: () => void }) {
    return (
        <button
            type="button"
            onClick={(event) => {
                event.stopPropagation()
                onClick()
            }}
            className="mt-4 w-full bg-green-1 text-white rounded-full py-2.5 text-[15px] font-bold cursor-pointer active:scale-[0.99] transition-transform"
        >
            پرداخت
        </button>
    )
}
